/**
 * Cursor → world raycast picking. Two jobs: object picking (`pick`, used by
 * the debug readout and click-to-talk) and ground-plane picking
 * (`pickGround`, used by RMB aim-tracking — see base-view/index.ts's frame
 * loop and character-animation-runtime.md §3.7).
 */
import * as THREE from 'three';
import type { ObjectId } from '../types';

export class Picker {
  private readonly ray = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();
  private readonly groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private readonly groundHit = new THREE.Vector3();

  pick(
    clientX: number,
    clientY: number,
    dom: HTMLElement,
    camera: THREE.Camera,
    pickables: THREE.Object3D[],
  ): ObjectId | null {
    const rect = dom.getBoundingClientRect();
    this.ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.ray.setFromCamera(this.ndc, camera);
    // Recursive: pickable roots are now composite groups (stall = counter +
    // posts + awning, etc). Walk up from the hit mesh to find the ancestor
    // that carries the objectId (set on the group root, not each child).
    const hits = this.ray.intersectObjects(pickables, true);
    for (const hit of hits) {
      let node: THREE.Object3D | null = hit.object;
      while (node) {
        const id = node.userData.objectId as ObjectId | undefined;
        if (id) return id;
        node = node.parent;
      }
    }
    return null;
  }

  /**
   * Ray from the given client (screen) coordinates against the ground plane
   * (y=0). Returns the hit point, or `null` if the ray doesn't intersect it
   * (near-parallel to, or pointing away from, the ground — shouldn't happen
   * at this game's fixed camera pitch, but callers must handle it: hold the
   * previous aim value rather than snapping facing to a default).
   *
   * Returns a reference to an internal, reused `Vector3` (same pattern as
   * `ray`/`ndc` above, not a fresh allocation) — read it synchronously; it's
   * overwritten on the next call.
   */
  pickGround(
    clientX: number,
    clientY: number,
    dom: HTMLElement,
    camera: THREE.Camera,
  ): THREE.Vector3 | null {
    const rect = dom.getBoundingClientRect();
    this.ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.ray.setFromCamera(this.ndc, camera);
    return this.ray.ray.intersectPlane(this.groundPlane, this.groundHit);
  }
}
